import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import * as interestsApi from '@/api/interests';
import type {
  ApiResponse,
  IdentityBoard,
  InterestType,
  MentoringPreference,
  InterestsBlocksData,
  RefInterest,
  InterestsResponse,
  ErasResponse,
  QuickPicksResponse,
  User,
} from '@/api/types';
import { Loader2, X, Search, ChevronRight, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import ProposeInterestInput from '@/components/board/ProposeInterestInput';

interface InterestsBlockProps {
  onComplete: () => void;
}

// Category groups for UI organization
const CATEGORY_GROUPS = {
  sports: { label: 'Sports & Games', emoji: '🏏', types: ['sport_modern', 'sport_traditional', 'indian_game'] },
  music: { label: 'Music', emoji: '🎵', types: ['music_classical', 'music_folk', 'music_devotional', 'music_film', 'music_contemporary'] },
  entertainment: { label: 'Entertainment', emoji: '🎬', types: ['entertainment_cinema', 'entertainment_tv', 'entertainment_ott'] },
  hobbies: { label: 'Hobbies', emoji: '🎨', types: ['hobby_dance', 'hobby_art', 'hobby_social', 'hobby_instrument'] },
  nostalgia: { label: 'Nostalgia', emoji: '📺', types: ['nostalgia_game', 'nostalgia_media', 'nostalgia_school', 'nostalgia_food', 'nostalgia_festival'] },
  spiritual: { label: 'Spiritual', emoji: '🙏', types: ['spiritual'] },
};

// Phase 13.6 — propose-input config. Only the four broad sections accept user
// proposals; nostalgia + spiritual stay catalog-only (curated era content + a
// single enum-shaped value, respectively). Defaults pick the most "general"
// type within each category — admins can re-classify on review.
const PROPOSE_CONFIG: Record<string, { interestType: InterestType; category: string }> = {
  sports: { interestType: 'sport_modern', category: 'sports' },
  music: { interestType: 'music_contemporary', category: 'music' },
  entertainment: { interestType: 'entertainment_cinema', category: 'entertainment' },
  hobbies: { interestType: 'hobby_social', category: 'hobbies' },
};

const MENTORING_OPTIONS: { value: MentoringPreference; label: string; description: string }[] = [
  { value: 'mentor', label: 'Happy to Mentor', description: "I can help others in my area of expertise" },
  { value: 'mentee', label: 'Looking for Mentorship', description: "I'd love guidance from experienced folks" },
  { value: 'both', label: 'Both', description: 'Open to mentoring and being mentored' },
  { value: 'neither', label: 'Not Right Now', description: 'Maybe later, not actively looking' },
];

// Get localized display name for an interest
function getDisplayName(interest: RefInterest, userState?: string, userLanguage?: string): string {
  // Priority 1: State-specific name
  if (userState && interest.stateNames[userState]) {
    return interest.stateNames[userState];
  }
  // Priority 2: Language-specific name
  if (userLanguage && interest.displayNames[userLanguage]) {
    return interest.displayNames[userLanguage];
  }
  // Priority 3: English canonical name
  return interest.canonicalNameEn;
}

export default function InterestsBlock({ onComplete }: InterestsBlockProps) {
  const queryClient = useQueryClient();

  // Current step: 'quickpicks' or 'explore'
  const [step, setStep] = useState<'quickpicks' | 'explore'>('quickpicks');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch user for state/language context
  const { data: userData } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get<ApiResponse<User>>('/auth/me'),
  });
  const userState = userData?.data?.state;

  // Fetch existing board data
  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: ['identity-board'],
    queryFn: () => api.get<ApiResponse<IdentityBoard>>('/identity-board'),
  });

  // Fetch interests from API
  const { data: interestsData } = useQuery({
    queryKey: ['interests'],
    queryFn: () => api.get<ApiResponse<InterestsResponse>>('/interests'),
  });

  // Fetch eras
  const { data: erasData } = useQuery({
    queryKey: ['interests-eras'],
    queryFn: () => api.get<ApiResponse<ErasResponse>>('/interests/eras'),
  });

  // Fetch quick picks based on user's state
  const { data: quickPicksData } = useQuery({
    queryKey: ['interests-quick-picks', userState],
    queryFn: () => api.get<ApiResponse<QuickPicksResponse>>('/interests/quick-picks', { limit: 12 }),
    enabled: step === 'quickpicks',
  });

  // Phase 13.6 — user's own pending proposals so we can render a "review"
  // badge on their pills (and surface their pending picks even though the
  // bulk /interests fetch filters status='approved').
  const { data: myProposalsData } = useQuery({
    queryKey: ['my-proposals', 'pending'],
    queryFn: () => interestsApi.getMyProposals({ status: 'pending' }),
  });

  const board = boardData?.data;
  const interestsBlocks = board?.interests;
  const interests = useMemo(() => interestsData?.data?.items || [], [interestsData]);
  const quickPicks = quickPicksData?.data?.items || [];
  const eras = erasData?.data?.items || [];
  const myPendingProposals = useMemo(() => myProposalsData?.data?.items || [], [myProposalsData]);
  const pendingCodes = useMemo(
    () => new Set(myPendingProposals.map((p) => p.code)),
    [myPendingProposals],
  );

  // Form state - selected interest codes
  const [selectedInterests, setSelectedInterests] = useState<Set<string>>(new Set());
  const [preferredEras, setPreferredEras] = useState<string[]>([]);
  const [mentoringPreference, setMentoringPreference] = useState<MentoringPreference | ''>('');

  // Locally-tracked freshly-proposed interests. The propose response gives us
  // canonical info we need for rendering before the next /interests refetch
  // includes them (and pending ones never will).
  const [proposedInterests, setProposedInterests] = useState<RefInterest[]>([]);

  // Single source of truth for "any RefInterest the user might have selected"
  // — bulk approved + their own pending + freshly-proposed-this-session.
  const allInterests = useMemo(() => {
    const map = new Map<string, RefInterest>();
    for (const i of interests) map.set(i.code, i);
    for (const p of myPendingProposals) if (!map.has(p.code)) map.set(p.code, p);
    for (const p of proposedInterests) if (!map.has(p.code)) map.set(p.code, p);
    return Array.from(map.values());
  }, [interests, myPendingProposals, proposedInterests]);

  // Initialize from existing board data. Entries are either RefInterest codes
  // (new format, direct match) or canonical names/aliases (legacy data from
  // before we switched to codes). Resolve both shapes so legacy users don't
  // lose selections when re-editing. We resolve against the unified
  // allInterests pool (approved + user's pending) so a user with a pending
  // proposal in their saved board doesn't silently lose it on re-edit.
  useEffect(() => {
    if (interestsBlocks) {
      const existingCodes = new Set<string>();

      const items = [
        ...(interestsBlocks.sports || []),
        ...(interestsBlocks.musicGenres || []),
        ...(interestsBlocks.entertainment || []),
        ...(interestsBlocks.hobbies || []),
      ];

      items.forEach(item => {
        const lower = item.toLowerCase();
        const match = allInterests.find(i =>
          i.code === item ||
          i.canonicalNameEn.toLowerCase() === lower ||
          i.searchAliases.includes(lower)
        );
        if (match) {
          existingCodes.add(match.code);
        }
      });

      setSelectedInterests(existingCodes);
      setMentoringPreference(interestsBlocks.mentoringPreference || '');
    }
  }, [interestsBlocks, allInterests]);

  // Filter interests based on search and category
  const filteredInterests = useMemo(() => {
    let filtered = interests;

    if (selectedCategory) {
      const types = CATEGORY_GROUPS[selectedCategory as keyof typeof CATEGORY_GROUPS]?.types || [];
      filtered = filtered.filter(i => types.includes(i.interestType));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(i =>
        i.canonicalNameEn.toLowerCase().includes(query) ||
        i.searchAliases.some(a => a.includes(query)) ||
        Object.values(i.displayNames).some(n => n.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [interests, selectedCategory, searchQuery]);

  // Group interests by type for display
  const interestsByCategory = useMemo(() => {
    const groups: Record<string, RefInterest[]> = {};

    for (const [key, config] of Object.entries(CATEGORY_GROUPS)) {
      groups[key] = filteredInterests.filter(i => config.types.includes(i.interestType));
    }

    return groups;
  }, [filteredInterests]);

  // Indian games (special section)
  const indianGames = useMemo(() =>
    interests.filter(i => i.interestType === 'indian_game'),
    [interests]
  );

  // Nostalgia items
  const nostalgiaItems = useMemo(() =>
    interests.filter(i => i.interestType.startsWith('nostalgia_')),
    [interests]
  );

  // Toggle interest selection
  const toggleInterest = (code: string) => {
    setSelectedInterests(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  // Toggle era preference
  const toggleEra = (eraCode: string) => {
    setPreferredEras(prev =>
      prev.includes(eraCode)
        ? prev.filter(e => e !== eraCode)
        : [...prev, eraCode]
    );
  };

  // Update mutation
  const updateInterestsBlocks = useMutation({
    mutationFn: async (data: InterestsBlocksData) => {
      return api.patch<ApiResponse<IdentityBoard>>('/identity-board/blocks/interests', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['identity-board'] });
      queryClient.invalidateQueries({ queryKey: ['identity-board-completeness'] });
      onComplete();
    },
  });

  // Phase 13.6 — user added/proposed an interest via ProposeInterestInput.
  // Cache it locally (so we can render it before any refetch) and add it to
  // the selection set.
  const handleProposed = (interest: RefInterest) => {
    setProposedInterests((prev) =>
      prev.find((p) => p.code === interest.code) ? prev : [...prev, interest],
    );
    setSelectedInterests((prev) => {
      const next = new Set(prev);
      next.add(interest.code);
      return next;
    });
  };

  const handleSubmit = () => {
    // Send RefInterest codes (e.g., "cricket_modern") rather than display
    // names. Display sites look up names via the /interests endpoint. The
    // init effect above already converts legacy name-based data to codes so
    // users re-editing old boards auto-heal on save.
    const selectedItems = allInterests.filter(i => selectedInterests.has(i.code));

    const sports = selectedItems
      .filter(i => ['sport_modern', 'sport_traditional', 'indian_game'].includes(i.interestType))
      .map(i => i.code);

    const musicGenres = selectedItems
      .filter(i => i.interestType.startsWith('music_'))
      .map(i => i.code);

    const entertainment = selectedItems
      .filter(i => i.interestType.startsWith('entertainment_'))
      .map(i => i.code);

    const hobbies = selectedItems
      .filter(i => i.interestType.startsWith('hobby_') || i.interestType.startsWith('nostalgia_') || i.interestType === 'spiritual')
      .map(i => i.code);

    updateInterestsBlocks.mutate({
      sports,
      musicGenres,
      entertainment,
      hobbies,
      mentoringPreference: mentoringPreference || undefined,
    });
  };

  const hasAnySelection = selectedInterests.size > 0;

  if (boardLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={() => setStep('quickpicks')}
          className={clsx(
            'px-3 py-1 rounded-full transition-colors',
            step === 'quickpicks' ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Quick Picks
        </button>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        <button
          onClick={() => setStep('explore')}
          className={clsx(
            'px-3 py-1 rounded-full transition-colors',
            step === 'explore' ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          Explore All
        </button>
      </div>

      {/* Selection Summary */}
      {selectedInterests.size > 0 && (
        <div className="p-3 bg-twins-light rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-twins-dark">
              {selectedInterests.size} interests selected
            </span>
            <button
              onClick={() => setSelectedInterests(new Set())}
              className="text-xs text-twins hover:text-twins-dark"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Array.from(selectedInterests).slice(0, 8).map(code => {
              const interest = allInterests.find(i => i.code === code);
              if (!interest) return null;
              const isPending = pendingCodes.has(code);
              return (
                <span
                  key={code}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-white text-twins-dark rounded-full text-xs"
                >
                  {interest.emoji} {getDisplayName(interest, userState)}
                  {isPending && (
                    <span className="text-[10px] text-amber-700" title="Pending review — visible to others once approved">
                      (review)
                    </span>
                  )}
                  <button onClick={() => toggleInterest(code)} className="hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
            {selectedInterests.size > 8 && (
              <span className="text-xs text-twins-dark">+{selectedInterests.size - 8} more</span>
            )}
          </div>
        </div>
      )}

      {/* STEP 1: Quick Picks */}
      {step === 'quickpicks' && (
        <div className="space-y-6">
          <div className="text-center">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-amber-500" />
            <h3 className="font-semibold text-lg">Quick Picks</h3>
            <p className="text-sm text-gray-500">Popular choices to get you started</p>
          </div>

          {/* Quick Pick Grid */}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {quickPicks.map(interest => (
              <button
                key={interest.code}
                onClick={() => toggleInterest(interest.code)}
                className={clsx(
                  'p-3 rounded-xl border-2 text-center transition-all',
                  selectedInterests.has(interest.code)
                    ? 'border-twins bg-twins-light'
                    : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                )}
              >
                <span className="text-2xl block mb-1">{interest.emoji || '🎯'}</span>
                <span className="text-xs font-medium block truncate">
                  {getDisplayName(interest, userState)}
                </span>
                {selectedInterests.has(interest.code) && (
                  <span className="text-[10px] text-twins">✓</span>
                )}
              </button>
            ))}
          </div>

          {/* Indian Games Section (Special) */}
          {indianGames.length > 0 && (
            <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                🎯 Indian Games
                <span className="text-xs font-normal text-amber-600">(Childhood favorites!)</span>
              </h4>
              <div className="flex flex-wrap gap-2">
                {indianGames.map(game => (
                  <button
                    key={game.code}
                    onClick={() => toggleInterest(game.code)}
                    className={clsx(
                      'px-3 py-1.5 rounded-full text-sm border-2 transition-all',
                      selectedInterests.has(game.code)
                        ? 'border-amber-500 bg-amber-100 text-amber-800'
                        : 'border-amber-200 text-amber-700 hover:border-amber-400 hover:bg-amber-50'
                    )}
                  >
                    {game.emoji} {getDisplayName(game, userState)}
                    {game.eraRelevance.length > 0 && (
                      <span className="ml-1 text-[10px] opacity-70">
                        {game.eraRelevance[0]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => setStep('explore')}
            className="w-full py-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
          >
            + Explore More Interests
          </button>
        </div>
      )}

      {/* STEP 2: Explore All */}
      {step === 'explore' && (
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search interests (try 'lagori', 'cricket', 'पिट्ठू')..."
              className="input pl-10 w-full"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={clsx(
                'px-3 py-1.5 rounded-full text-sm transition-colors',
                !selectedCategory
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              All
            </button>
            {Object.entries(CATEGORY_GROUPS).map(([key, config]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={clsx(
                  'px-3 py-1.5 rounded-full text-sm transition-colors',
                  selectedCategory === key
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {config.emoji} {config.label}
              </button>
            ))}
          </div>

          {/* Era Preferences */}
          {eras.length > 0 && (
            <div>
              <label className="label text-sm">Your Era (for nostalgia matching)</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {eras.map(era => (
                  <button
                    key={era.code}
                    onClick={() => toggleEra(era.code)}
                    className={clsx(
                      'px-3 py-1.5 rounded-full text-sm border transition-colors',
                      preferredEras.includes(era.code)
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    )}
                  >
                    {era.nameEn}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Interests by Category */}
          {(selectedCategory ? [selectedCategory] : Object.keys(CATEGORY_GROUPS)).map(catKey => {
            const config = CATEGORY_GROUPS[catKey as keyof typeof CATEGORY_GROUPS];
            const items = interestsByCategory[catKey] || [];
            const proposeConfig = PROPOSE_CONFIG[catKey];
            if (items.length === 0 && !proposeConfig) return null;

            return (
              <div key={catKey}>
                <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span>{config.emoji}</span>
                  {config.label}
                  <span className="text-xs text-gray-400">({items.length})</span>
                </h4>
                <div className="flex flex-wrap gap-2 items-center">
                  {items.map(interest => (
                    <button
                      key={interest.code}
                      onClick={() => toggleInterest(interest.code)}
                      className={clsx(
                        'px-3 py-1.5 rounded-full text-sm border transition-colors',
                        selectedInterests.has(interest.code)
                          ? 'border-twins bg-twins-light text-twins-dark'
                          : 'border-gray-200 text-gray-700 hover:border-twins hover:bg-twins-light/50'
                      )}
                    >
                      {interest.emoji && <span className="mr-1">{interest.emoji}</span>}
                      {getDisplayName(interest, userState)}
                      {interest.isPopular && <span className="ml-1 text-amber-500">★</span>}
                    </button>
                  ))}
                  {proposeConfig && (
                    <ProposeInterestInput
                      interestType={proposeConfig.interestType}
                      category={proposeConfig.category}
                      excludeCodes={Array.from(selectedInterests)}
                      onAdd={handleProposed}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Nostalgia Section (Special styling) */}
          {(!selectedCategory || selectedCategory === 'nostalgia') && nostalgiaItems.length > 0 && (
            <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
              <h4 className="font-semibold text-purple-800 mb-3">
                📺 Nostalgia Corner
                <span className="text-xs font-normal text-purple-600 ml-2">
                  Connect over shared memories
                </span>
              </h4>
              <div className="flex flex-wrap gap-2">
                {nostalgiaItems.map(item => (
                  <button
                    key={item.code}
                    onClick={() => toggleInterest(item.code)}
                    className={clsx(
                      'px-3 py-1.5 rounded-full text-sm border transition-colors',
                      selectedInterests.has(item.code)
                        ? 'border-purple-500 bg-purple-100 text-purple-800'
                        : 'border-purple-200 text-purple-700 hover:border-purple-400'
                    )}
                  >
                    {item.emoji} {getDisplayName(item, userState)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mentoring Preference */}
      <div>
        <label className="label">Mentoring Preference</label>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {MENTORING_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setMentoringPreference(option.value)}
              className={clsx(
                'p-3 rounded-lg border-2 text-left transition-all',
                mentoringPreference === option.value
                  ? 'border-twins bg-twins-light'
                  : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <span className="font-medium block">{option.label}</span>
              <span className="text-xs text-gray-500">{option.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Submit */}
      <div className="pt-4">
        <button
          onClick={handleSubmit}
          disabled={!hasAnySelection || updateInterestsBlocks.isPending}
          className="btn-primary w-full py-3 disabled:opacity-50"
        >
          {updateInterestsBlocks.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Save & Continue'
          )}
        </button>
        {!hasAnySelection && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            Please select at least one interest to continue
          </p>
        )}
        {updateInterestsBlocks.isError && (
          <p className="text-sm text-red-600 mt-2 text-center">
            Failed to save. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
